import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { HelpOutline as HelpOutlineIcon } from "@mui/icons-material";
import { Logo } from "@storyworm/ssup-ui";
import type { SsupSession } from "../auth/useSsupSession";
import { UniverseList } from "../universe/UniverseList";

/**
 * The signed-in portal chrome: a top bar (logo, role, account, guide/sign-out)
 * over the access-gated content. Derives the display role/subtitle from the
 * session and either shows the universe list or a "not provisioned" notice.
 */
export function PortalShell({
  session,
  onOpenDocs,
  onSignOut,
}: {
  session: SsupSession;
  onOpenDocs: () => void;
  onSignOut: () => void;
}) {
  const { user, claims, isAdmin } = session;
  const isPartner = claims?.role === "partner";
  const hasAccess = isAdmin || isPartner;

  const roleLabel = isAdmin ? "Admin" : isPartner ? "Partner" : "No access";
  const subtitle = isAdmin
    ? "All client universes"
    : isPartner
      ? "Your organization's universes"
      : "Awaiting provisioning";

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Logo maxWidth={200} sx={{ width: 200 }} />
          <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
          <Typography variant="h1" noWrap>
            Partner Validation Portal
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Partner guide">
            <IconButton size="small" color="inherit" onClick={onOpenDocs} aria-label="Partner guide">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
          <Chip
            label={roleLabel}
            size="small"
            color={hasAccess ? "primary" : "default"}
            variant="outlined"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: 240, display: { xs: "none", sm: "block" } }}
          >
            {user?.email ?? user?.uid}
          </Typography>
          <Button size="small" color="inherit" onClick={onSignOut}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h1" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>

        {hasAccess ? (
          <UniverseList isAdmin={isAdmin} orgId={claims?.org_id} />
        ) : (
          <Typography color="text.secondary">
            Your account isn’t provisioned yet. An admin needs to grant access before you can
            validate a universe.
          </Typography>
        )}
      </Container>
    </Box>
  );
}
